import { useEffect, useState } from 'react';

import { toast } from 'react-toastify';

import { useFormikForm } from '#/common/hooks';
import { useAppContext } from '#/context';
import { apiEndpoints, queryKeys, usePatchMutation } from '#/services';
import { IMAGE_SIZE, UserProfileValidationSchema, userProfileInInitialValues } from '#/utils';

export function useProfile() {
    const { user } = useAppContext();

    const [imagePreview, setImagePreview] = useState(null);
    const [initialValues, setInitialValues] = useState({});
    const [isChangePassword, setIsChangePassword] = useState(false);

    const { mutate: updateProfileApiCall, isPending: updateProfileApiLoading } = usePatchMutation({
        url: apiEndpoints.UPDATE_USER,
        queryKey: [queryKeys?.USER],
        successMessage: 'Profile updated successfully!',
        errorMessage: 'Failed to update profile!',
    });

    const handleProfileAPI = async values => {
        const formData = new FormData();

        if (values.firstName !== initialValues.firstName) {
            formData.append('first_name', values?.firstName);
        }

        if (values.lastName !== initialValues.lastName) {
            formData.append('last_name', values?.lastName);
        }

        if (values.profilePicture !== initialValues.profilePicture) {
            formData.append('profile_picture', values?.profilePicture);
        }

        updateProfileApiCall({
            payload: formData,
        });
    };

    const handleImage = e => {
        const file = e.target.files[0];
        const validTypes = ['image/jpg', 'image/png', 'image/jpeg'];

        if (file) {
            if (file.size > IMAGE_SIZE) {
                setFieldError('profilePicture', 'File size should be less than 1MB');
                toast.warning('File size should be less than 1MB');
            } else if (!validTypes.includes(file.type)) {
                setFieldError('profilePicture', 'Only JPG, JPEG and PNG formats are allowed');
                toast.warning('Only JPG, JPEG and PNG formats are allowed');
            } else {
                setProfileValues('profilePicture', file);
                const imageURL = URL.createObjectURL(file);
                setImagePreview(imageURL);
            }
        }
    };

    const handleDeleteImage = () => {
        setImagePreview(null);
        setProfileValues('profilePicture', '');
    };
    const handleChangePassword = () => {
        setIsChangePassword(true);
    };

    const {
        handleChange: handleChangeProfile,
        handleSubmit: handleSubmitProfile,
        errors: profileErrors,
        touched: profileTouched,
        values: profileValues,
        setFieldValue: setProfileValues,
        resetForm: resetProfileForm,
        setFieldError,
    } = useFormikForm(userProfileInInitialValues, handleProfileAPI, UserProfileValidationSchema);

    useEffect(() => {
        if (user) {
            resetProfileForm({
                values: {
                    firstName: user.firstName || '',
                    lastName: user.lastName || '',
                    email: user.email || '',
                    profilePicture: user.profilePicture || '',
                    businessName: user.businessName || '',
                },
            });
            setImagePreview(user.profilePicture || '');
            setInitialValues({
                firstName: user.firstName || '',
                lastName: user.lastName || '',
                email: user.email || '',
                profilePicture: user.profilePicture || '',
                businessName: user.businessName || '',
            });
        }
    }, [user, resetProfileForm]);

    return {
        handleChangeProfile,
        handleSubmitProfile,
        profileErrors,
        profileTouched,
        profileValues,
        setProfileValues,
        updateProfileApiLoading,
        handleImage,
        handleDeleteImage,
        imagePreview,
        isChangePassword,
        handleChangePassword,
        setIsChangePassword,
    };
}
